import {
  isReservedProductBarcode,
  parseBahtToSatang,
  ScannerBurstNormalizer,
} from '@pos/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { controlClass } from '../../components/fieldControl';
import { Modal } from '../../components/Modal';
import { listCategories, listProducts } from '../../data/fetchers';
import { upsertProduct } from '../../data/mutators';
import { fmtAmount, fmtInt } from '../../lib/format';
import { canEditMasterData } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type { Product, ProductUnit } from '../../types/domain';
import { ProductHeader } from './ProductHeader';
import { ProductImageField } from './ProductImageField';
import {
  absoluteToRelativeFactors,
  baseCostFromSelectedUnit,
  classifyPriceHealth,
  derivedUnitCost,
  type PriceHealthStatus,
  relativeToAbsoluteFactors,
} from './productUnitPricing';

type ProductFields = Omit<Product, 'baseUnit' | 'baseUnitId' | 'units'>;

interface UnitDraft extends ProductUnit {
  id: string;
  relativeInput: string;
  priceInput: string;
}

let unitDraftSequence = 0;

const newUnitId = (): string => {
  const generated = globalThis.crypto?.randomUUID?.();
  if (generated) return `product-unit:${generated}`;
  unitDraftSequence += 1;
  return `product-unit:${Date.now()}:${unitDraftSequence}`;
};

const createUnitDraft = (
  unit: ProductUnit = { unit: 'ชิ้น', factor: 1, price: 0, barcode: '' },
  relativeInput = '1',
): UnitDraft => ({
  ...unit,
  id: unit.id ?? newUnitId(),
  relativeInput,
  priceInput: moneyInputValue(unit.price),
});

const blank = (): ProductFields => ({
  id: '',
  name: '',
  categoryId: '',
  active: true,
  minStock: 0,
  imageUrl: '',
  cost: 0,
});

const THAI_NUMBER_ROW: Record<string, string> = {
  ๅ: '1',
  ภ: '4',
  ถ: '5',
  'ุ': '6',
  'ึ': '7',
  ค: '8',
  ต: '9',
  จ: '0',
};
const THAI_NUMBER_ROW_SIGNALS = new Set([
  'ๅ',
  'ภ',
  'ถ',
  'ุ',
  'ึ',
  'ค',
  'ต',
  'จ',
]);

export function normalizeBarcodeKeyboardValue(value: string): string {
  const characters = [...value];
  const hasUnambiguousThaiKey = characters.some((char) =>
    THAI_NUMBER_ROW_SIGNALS.has(char),
  );
  if (!hasUnambiguousThaiKey) {
    return value;
  }
  return characters
    .map((char) =>
      THAI_NUMBER_ROW_SIGNALS.has(char) ? THAI_NUMBER_ROW[char] : char,
    )
    .join('');
}

function moneyInputValue(satang: number): string {
  if (!satang) return '';
  return String(satang / 100);
}

function FieldLabel({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={`text-sm-label font-semibold text-ink-2 ${className}`}>
      {children}
    </span>
  );
}

// Shared by the unit header row and every unit row so columns line up; fixed
// tracks for the last two keep rows with/without a delete button identical.
const UNIT_GRID =
  'xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)_7.5rem_2.75rem]';

function BarcodeInput({
  index,
  value,
  disabled,
  error,
  onChange,
}: {
  index: number;
  value: string;
  disabled: boolean;
  error?: string;
  onChange: (value: string) => void;
}) {
  const scannerBurst = useRef(new ScannerBurstNormalizer());

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const normalizedScan = scannerBurst.current.complete(event.nativeEvent);
      if (normalizedScan) onChange(normalizedScan);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    scannerBurst.current.push(event.nativeEvent);
  };

  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <FieldLabel className="xl:sr-only">บาร์โค้ด (ไม่บังคับ)</FieldLabel>
      <input
        aria-label={`บาร์โค้ดแถว ${index + 1}`}
        disabled={disabled}
        type="text"
        value={value}
        onChange={(event) =>
          onChange(normalizeBarcodeKeyboardValue(event.target.value))
        }
        onKeyDown={handleKeyDown}
        className={controlClass(
          'md',
          `w-full font-mono disabled:bg-bg-warm ${
            error
              ? 'border-danger focus:border-danger focus:ring-danger/20'
              : ''
          }`,
        )}
        placeholder="สแกนหรือกรอกบาร์โค้ด"
      />
      {error ? (
        <span role="alert" className="text-xs font-semibold text-danger">
          {error}
        </span>
      ) : value ? (
        <span className="text-xs text-ink-3">
          ขายได้ทั้งสแกนและเลือกที่ POS
        </span>
      ) : (
        <span className="text-xs text-ink-3">
          ไม่มีบาร์โค้ด — ขายผ่านการค้นหาชื่อที่ POS
        </span>
      )}
    </label>
  );
}

const healthStyle: Record<
  PriceHealthStatus,
  { label: string; className: string }
> = {
  unknown: { label: 'รอข้อมูล', className: 'bg-bg-warm text-ink-3' },
  loss: { label: 'ขาดทุน', className: 'bg-danger-light text-danger' },
  'low-margin': { label: 'กำไรต่ำ', className: 'bg-warn-light text-warn' },
  healthy: { label: 'ปกติ', className: 'bg-ok-light text-ok' },
};

export function ProductFormView() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useSession((state) => state.user);
  const canEdit = user ? canEditMasterData(user) : false;

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });

  const [form, setForm] = useState<ProductFields>(blank());
  const [unitDrafts, setUnitDrafts] = useState<UnitDraft[]>(() => [
    createUnitDraft(),
  ]);
  const [minStockInput, setMinStockInput] = useState('');
  const [costUnitId, setCostUnitId] = useState(() => unitDrafts[0]!.id);
  const [costInput, setCostInput] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [lossConfirmOpen, setLossConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isNew) return;
    const baseUnit = createUnitDraft();
    setForm(blank());
    setUnitDrafts([baseUnit]);
    setMinStockInput('');
    setCostUnitId(baseUnit.id);
    setCostInput('');
    setSubmitError('');
  }, [id, isNew]);

  useEffect(() => {
    if (!isNew || categories.length === 0) return;
    setForm((current) =>
      categories.some((category) => category.id === current.categoryId)
        ? current
        : { ...current, categoryId: categories[0]!.id },
    );
  }, [categories, isNew]);

  useEffect(() => {
    if (isNew) return;
    const found = products.find((product) => product.id === id);
    if (!found) return;
    const sortedUnits = [...found.units].sort(
      (left, right) => left.factor - right.factor,
    );
    const relativeFactors = absoluteToRelativeFactors(
      sortedUnits.map((unit) => unit.factor),
    );
    const nextUnitDrafts = sortedUnits.map((unit, index) =>
      createUnitDraft(unit, String(relativeFactors[index] ?? 1)),
    );
    const nextCostUnit = nextUnitDrafts.at(-1) ?? createUnitDraft();
    setForm({
      id: found.id,
      name: found.name,
      categoryId: found.categoryId,
      active: found.active,
      minStock: found.minStock,
      imageUrl: found.imageUrl,
      cost: found.cost,
    });
    setUnitDrafts(nextUnitDrafts.length > 0 ? nextUnitDrafts : [nextCostUnit]);
    setMinStockInput(String(found.minStock ?? 0));
    setCostUnitId(nextCostUnit.id);
    setCostInput(moneyInputValue((found.cost ?? 0) * nextCostUnit.factor));
    setSubmitError('');
  }, [id, isNew, products]);

  const mutation = useMutation({
    mutationFn: upsertProduct,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['products-page'] });
      navigate('/products');
    },
  });

  const set = <K extends keyof ProductFields>(
    key: K,
    value: ProductFields[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  const setUnit = (unitId: string, patch: Partial<UnitDraft>) =>
    setUnitDrafts((current) =>
      current.map((unit) =>
        unit.id === unitId ? { ...unit, ...patch } : unit,
      ),
    );

  const syncUnitDrafts = (
    nextDrafts: UnitDraft[],
    selectedCostUnitId = costUnitId,
    selectedCostInput = costInput,
  ) => {
    const absoluteFactors = relativeToAbsoluteFactors(
      nextDrafts.map((unit, index) =>
        index === 0 ? 1 : Number(unit.relativeInput),
      ),
    );
    const units = nextDrafts.map((unit, index) => ({
      ...unit,
      relativeInput: index === 0 ? '1' : unit.relativeInput,
      factor: absoluteFactors[index] ?? Number.NaN,
    }));
    const selectedFactor =
      units.find((unit) => unit.id === selectedCostUnitId)?.factor ?? 1;
    const selectedCost = selectedCostInput
      ? parseBahtToSatang(selectedCostInput)
      : 0;
    setUnitDrafts(units);
    setForm((current) => ({
      ...current,
      cost: baseCostFromSelectedUnit(selectedCost, selectedFactor),
    }));
  };

  const addUnit = () => {
    const previousLargest = unitDrafts.at(-1)!;
    const nextUnit = createUnitDraft(
      { unit: '', factor: previousLargest.factor, price: 0, barcode: '' },
      '1',
    );
    const followLargest = costUnitId === previousLargest.id;
    const nextCostUnitId = followLargest ? nextUnit.id : costUnitId;
    syncUnitDrafts([...unitDrafts, nextUnit], nextCostUnitId);
    if (followLargest) {
      setCostUnitId(nextUnit.id);
      setCostInput(moneyInputValue((form.cost ?? 0) * nextUnit.factor));
    }
  };

  const removeUnit = (unitId: string) => {
    const index = unitDrafts.findIndex((unit) => unit.id === unitId);
    if (index === 0) return;
    const nextUnits = unitDrafts.filter((unit) => unit.id !== unitId);
    const nextCostUnitId =
      costUnitId === unitId ? nextUnits.at(-1)!.id : costUnitId;
    if (costUnitId === unitId) {
      const absoluteFactors = relativeToAbsoluteFactors(
        nextUnits.map((unit, unitIndex) =>
          unitIndex === 0 ? 1 : Number(unit.relativeInput),
        ),
      );
      const nextCostIndex = nextUnits.findIndex(
        (unit) => unit.id === nextCostUnitId,
      );
      const nextCostInput = moneyInputValue(
        (form.cost ?? 0) * (absoluteFactors[nextCostIndex] ?? 1),
      );
      setCostInput(nextCostInput);
      syncUnitDrafts(nextUnits, nextCostUnitId, nextCostInput);
    } else {
      syncUnitDrafts(nextUnits, nextCostUnitId);
    }
    setCostUnitId(nextCostUnitId);
  };

  const barcodeErrors = useMemo(() => {
    const errors = new Map<number, string>();
    const seen = new Map<string, number>();
    unitDrafts.forEach((unit, index) => {
      const barcode = unit.barcode?.trim();
      if (!barcode) return;
      if (isReservedProductBarcode(barcode)) {
        errors.set(
          index,
          'บาร์โค้ดขึ้นต้นด้วย QTY ไม่ได้ เนื่องจากสงวนไว้สำหรับคำสั่งจำนวนของ POS',
        );
        return;
      }
      const firstIndex = seen.get(barcode);
      if (firstIndex !== undefined) {
        errors.set(index, `บาร์โค้ดซ้ำกับหน่วยแถว ${firstIndex + 1}`);
        errors.set(firstIndex, `บาร์โค้ดซ้ำกับหน่วยแถว ${index + 1}`);
      } else {
        seen.set(barcode, index);
      }
      for (const product of products) {
        if (product.id === form.id) continue;
        const ownerUnit = product.units.find(
          (candidate) => candidate.barcode?.trim() === barcode,
        );
        if (ownerUnit) {
          errors.set(
            index,
            `ถูกใช้แล้วกับ ${product.name} (${ownerUnit.unit})`,
          );
          break;
        }
      }
    });
    return errors;
  }, [form.id, products, unitDrafts]);

  const unitHealth = unitDrafts.map((unit) => {
    const cost = derivedUnitCost(form.cost ?? 0, unit.factor);
    return { cost, ...classifyPriceHealth(unit.price, cost) };
  });
  const lossCount = unitHealth.filter((item) => item.status === 'loss').length;
  const warningCount = unitHealth.filter(
    (item) => item.status === 'low-margin',
  ).length;

  // A new product gets its id on first photo upload so the photo is stored
  // under it; saving reuses the same id.
  const ensureProductId = () => {
    if (form.id) return form.id;
    const nextId = `p${Date.now()}`;
    set('id', nextId);
    return nextId;
  };

  const saveProduct = () => {
    const baseUnit = unitDrafts[0]!;
    const units = unitDrafts.map(
      ({ relativeInput: _relativeInput, priceInput: _priceInput, ...unit }) =>
        unit,
    );
    mutation.mutate({
      ...form,
      id: form.id || `p${Date.now()}`,
      baseUnit: baseUnit.unit,
      baseUnitId: baseUnit.id,
      units,
    });
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    setSubmitError('');
    if (barcodeErrors.size > 0) {
      setSubmitError('กรุณาแก้ไขบาร์โค้ดที่ไม่ถูกต้องก่อนบันทึก');
      return;
    }
    if (
      unitDrafts.some(
        (unit) => !Number.isFinite(unit.factor) || unit.factor <= 0,
      )
    ) {
      setSubmitError('กรุณากรอกจำนวนต่อหน่วยให้ถูกต้อง');
      return;
    }
    if (lossCount > 0) {
      setLossConfirmOpen(true);
      return;
    }
    saveProduct();
  };

  const healthyCount = unitHealth.filter(
    (item) => item.status === 'healthy',
  ).length;
  const baseUnitName = unitDrafts[0]?.unit || 'หน่วยเล็กสุด';
  const formId = useId();
  const footerStatus = !canEdit
    ? { label: 'อ่านอย่างเดียว', dot: 'bg-ink-3' }
    : lossCount > 0
      ? { label: `${lossCount} หน่วยราคาต่ำกว่าทุน`, dot: 'bg-danger' }
      : warningCount > 0
        ? { label: `${warningCount} หน่วยกำไรต่ำกว่า 10%`, dot: 'bg-warn' }
        : healthyCount > 0
          ? { label: `ราคาปกติทั้ง ${healthyCount} หน่วย`, dot: 'bg-ok' }
          : { label: 'กรอกต้นทุนและราคาเพื่อดูกำไร', dot: 'bg-ink-4' };

  return (
    <div className="bo-fade">
      <div className="mx-auto w-full max-w-6xl">
        <ProductHeader
          nav={false}
          title={isNew ? 'เพิ่มสินค้า' : 'แก้ไขสินค้า'}
          subtitle={
            canEdit
              ? 'กำหนดหน่วย ต้นทุน และราคาขายจากของที่ใช้จริงในร้าน'
              : 'อ่านอย่างเดียว (ไม่มีสิทธิ์แก้ไข)'
          }
          status={
            mutation.isPending
              ? { label: 'กำลังบันทึกสินค้า', tone: 'neutral' }
              : lossCount > 0
                ? { label: `${lossCount} หน่วยราคาต่ำกว่าทุน`, tone: 'danger' }
                : warningCount > 0
                  ? { label: `${warningCount} หน่วยกำไรต่ำ`, tone: 'warn' }
                  : undefined
          }
        />

        <form id={formId} onSubmit={onSubmit} className="space-y-5">
          <Card title="ข้อมูลสินค้า" className="p-5">
            {/* Fields stack in three rows (name / category + min stock / active) so
                the column matches the photo's height instead of leaving a gap. */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-[9rem_minmax(0,1fr)]">
              <ProductImageField
                imageUrl={form.imageUrl || undefined}
                productName={form.name}
                disabled={!canEdit}
                ensureProductId={ensureProductId}
                onChange={(imageUrl) => set('imageUrl', imageUrl)}
              />
              <div className="grid grid-cols-1 content-start gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1.5 md:col-span-2">
                  <FieldLabel>ชื่อสินค้า</FieldLabel>
                  <input
                    aria-label="ชื่อสินค้า"
                    disabled={!canEdit}
                    value={form.name}
                    onChange={(event) => set('name', event.target.value)}
                    className={controlClass('md', 'w-full disabled:bg-bg-warm')}
                    placeholder="เช่น กล้วยหอม, ขนม Homemade"
                    required
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <FieldLabel>หมวดหมู่</FieldLabel>
                  <select
                    aria-label="หมวดหมู่"
                    disabled={!canEdit || categories.length === 0}
                    value={form.categoryId}
                    onChange={(event) => set('categoryId', event.target.value)}
                    required
                    className={controlClass('md', 'w-full disabled:bg-bg-warm')}
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5">
                  <FieldLabel>สต๊อกขั้นต่ำ ({baseUnitName})</FieldLabel>
                  <input
                    aria-label="สต๊อกขั้นต่ำ"
                    disabled={!canEdit}
                    type="number"
                    min={0}
                    value={minStockInput}
                    onChange={(event) => {
                      setMinStockInput(event.target.value);
                      set(
                        'minStock',
                        event.target.value === ''
                          ? 0
                          : Number(event.target.value),
                      );
                    }}
                    className={controlClass(
                      'md',
                      'num w-full disabled:bg-bg-warm',
                    )}
                    placeholder="0"
                  />
                </label>

                <label className="flex min-h-11 items-center gap-2.5 md:col-span-2">
                  <input
                    type="checkbox"
                    disabled={!canEdit}
                    checked={form.active}
                    onChange={(event) => set('active', event.target.checked)}
                    className="h-4.5 w-4.5 accent-primary"
                  />
                  <span className="text-body font-medium text-ink">
                    เปิดใช้งานขายสินค้านี้
                  </span>
                </label>
              </div>
            </div>
          </Card>

          <Card title="หน่วยขายและราคา" className="p-5">
            <p className="-mt-1 mb-4 text-sm-label text-ink-3">
              ราคากลางทุกสาขา · เริ่มจากหน่วยเล็กที่สุด
              แล้วเพิ่มหน่วยที่ใหญ่ขึ้น
            </p>
            <div className="mb-5 grid grid-cols-1 gap-4 rounded-card bg-surface-2 p-4 sm:grid-cols-2 lg:grid-cols-[15rem_15rem_minmax(0,1fr)]">
              <label className="flex flex-col gap-1.5">
                <FieldLabel>ต้นทุนนี้เป็นราคาของหน่วย</FieldLabel>
                <select
                  aria-label="หน่วยของต้นทุน"
                  disabled={!canEdit}
                  value={costUnitId}
                  onChange={(event) => {
                    const nextUnitId = event.target.value;
                    const nextUnit = unitDrafts.find(
                      (unit) => unit.id === nextUnitId,
                    );
                    setCostUnitId(nextUnitId);
                    setCostInput(
                      moneyInputValue(
                        (form.cost ?? 0) * (nextUnit?.factor ?? 1),
                      ),
                    );
                  }}
                  className={controlClass('md', 'w-full disabled:bg-bg-warm')}
                >
                  {unitDrafts.map((unit, index) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.unit || `หน่วย ${index + 1}`}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <FieldLabel>ต้นทุนตั้งต้น (บาท)</FieldLabel>
                <input
                  aria-label="ต้นทุนสินค้า"
                  disabled={!canEdit}
                  type="number"
                  min={0}
                  step="0.01"
                  value={costInput}
                  onChange={(event) => {
                    const value = event.target.value;
                    setCostInput(value);
                    set(
                      'cost',
                      baseCostFromSelectedUnit(
                        value ? parseBahtToSatang(value) : 0,
                        unitDrafts.find((unit) => unit.id === costUnitId)
                          ?.factor ?? 1,
                      ),
                    );
                  }}
                  className={controlClass(
                    'md',
                    'num w-full text-right disabled:bg-bg-warm',
                  )}
                  placeholder="เช่น 2160.00"
                />
              </label>

              <div className="flex flex-col gap-1.5 sm:col-span-2 lg:col-span-1 lg:items-end lg:text-right">
                <FieldLabel>ต้นทุนต่อ{baseUnitName}</FieldLabel>
                <output
                  aria-label="ต้นทุนต่อหน่วยฐาน"
                  className="num flex min-h-11 items-center text-h3 font-semibold text-ink"
                >
                  {fmtAmount(form.cost ?? 0)} บาท
                </output>
              </div>
            </div>

            {/* Column headers replace the per-row labels once rows sit on one line. */}
            <div
              aria-hidden="true"
              className={`hidden gap-x-4 border-b border-line pb-2 text-sm-label font-semibold text-ink-2 xl:grid ${UNIT_GRID}`}
            >
              <span>ชื่อหน่วย</span>
              <span>บรรจุ</span>
              <span>บาร์โค้ด (ไม่บังคับ)</span>
              <span className="text-right">ราคาขาย (บาท)</span>
              <span className="text-right">กำไรขั้นต้น</span>
              <span />
            </div>
            <div className="divide-y divide-line-2">
              {unitDrafts.map((unit, index) => {
                const previousUnit = unitDrafts[index - 1];
                const health = unitHealth[index]!;
                const style = healthStyle[health.status];
                return (
                  <div
                    key={unit.id}
                    className={`grid grid-cols-1 items-start gap-x-4 gap-y-3 py-4 sm:grid-cols-2 ${UNIT_GRID}`}
                  >
                    <label className="flex min-w-0 flex-col gap-1.5">
                      <FieldLabel className="xl:sr-only">ชื่อหน่วย</FieldLabel>
                      <input
                        className={controlClass('md', 'w-full')}
                        aria-label={`หน่วยแถว ${index + 1}`}
                        disabled={!canEdit}
                        value={unit.unit}
                        onChange={(event) =>
                          setUnit(unit.id, { unit: event.target.value })
                        }
                        required
                      />
                    </label>
                    {index > 0 ? (
                      <label className="flex min-w-0 flex-col gap-1.5">
                        <FieldLabel className="xl:sr-only">
                          บรรจุ ({previousUnit?.unit || 'หน่วยก่อนหน้า'})
                        </FieldLabel>
                        <input
                          className={controlClass('md', 'num w-full')}
                          aria-label={`จำนวนต่อหน่วยแถว ${index + 1}`}
                          type="number"
                          min="0.001"
                          step="any"
                          disabled={!canEdit}
                          value={unit.relativeInput}
                          onChange={(event) =>
                            syncUnitDrafts(
                              unitDrafts.map((candidate) =>
                                candidate.id === unit.id
                                  ? {
                                      ...candidate,
                                      relativeInput: event.target.value,
                                    }
                                  : candidate,
                              ),
                            )
                          }
                          required
                        />
                        <span className="text-xs text-ink-3">
                          {previousUnit?.unit || 'หน่วยก่อนหน้า'} ·{' '}
                          <span>
                            รวม{' '}
                            {Number.isFinite(unit.factor)
                              ? fmtInt(unit.factor)
                              : '—'}{' '}
                            {baseUnitName}
                          </span>
                        </span>
                      </label>
                    ) : (
                      <div className="flex min-w-0 flex-col gap-1.5">
                        <FieldLabel className="xl:sr-only">บรรจุ</FieldLabel>
                        <span className="flex min-h-11 items-center font-medium text-ink">
                          หน่วยฐาน
                        </span>
                        <span className="text-xs text-ink-3">
                          นับสต๊อกด้วยหน่วยนี้
                        </span>
                      </div>
                    )}
                    <BarcodeInput
                      index={index}
                      value={unit.barcode ?? ''}
                      disabled={!canEdit}
                      error={barcodeErrors.get(index)}
                      onChange={(barcode) => setUnit(unit.id, { barcode })}
                    />
                    <label className="flex min-w-0 flex-col gap-1.5">
                      <FieldLabel className="xl:sr-only">
                        ราคาขาย (บาท)
                      </FieldLabel>
                      <input
                        className={controlClass('md', 'num w-full text-right')}
                        aria-label={`ราคาแถว ${index + 1}`}
                        disabled={!canEdit}
                        type="number"
                        min={0}
                        step="0.01"
                        value={unit.priceInput}
                        onChange={(event) => {
                          const value = event.target.value;
                          setUnit(unit.id, {
                            priceInput: value,
                            price: value ? parseBahtToSatang(value) : 0,
                          });
                        }}
                        required
                      />
                      <span className="text-xs text-ink-3 xl:text-right">
                        ทุน {fmtAmount(health.cost)} บาท
                      </span>
                    </label>
                    <div className="flex min-w-0 flex-col gap-1.5 xl:items-end">
                      <FieldLabel className="xl:sr-only">
                        กำไรขั้นต้น
                      </FieldLabel>
                      <span
                        className={`num inline-flex min-h-11 w-fit items-center rounded-btn px-2.5 font-semibold ${style.className}`}
                      >
                        {health.marginPct === null
                          ? 'รอข้อมูล'
                          : `${health.marginPct.toFixed(1)}%`}
                      </span>
                      <span className="num text-xs text-ink-3">
                        {health.profit === null
                          ? 'ยังไม่มีต้นทุนหรือราคา'
                          : `${fmtAmount(health.profit)} บาท`}
                      </span>
                    </div>
                    <div className="flex sm:col-span-2 xl:col-span-1 xl:justify-end">
                      {canEdit && index > 0 && (
                        <button
                          type="button"
                          aria-label="ลบหน่วย"
                          title="ลบหน่วย"
                          onClick={() => removeUnit(unit.id)}
                          className="inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-btn px-2.5 text-sm-label font-medium text-ink-3 transition-colors hover:bg-danger-light hover:text-danger focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            className="h-4 w-4"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                          <span aria-hidden="true" className="xl:hidden">
                            ลบหน่วย
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {canEdit && (
              <div className="mt-2 border-t border-line-2 pt-4">
                <Btn kind="default" type="button" onClick={addUnit}>
                  + เพิ่มหน่วย
                </Btn>
              </div>
            )}
          </Card>

          {(submitError || mutation.error) && (
            <div
              role="alert"
              className="rounded-card border border-danger/20 bg-danger-light px-4 py-3 text-body font-semibold text-danger"
            >
              {submitError ||
                (mutation.error instanceof Error
                  ? mutation.error.message
                  : 'บันทึกสินค้าไม่สำเร็จ')}
            </div>
          )}
        </form>
      </div>
      {/* Outside the max-w container so it bleeds to the main edges (cancels
            Shell's p-4/6/8); the inner row keeps the content width so buttons
            line up with the cards. Submits the form via its id. */}
      <div className="sticky -bottom-4 z-10 -mx-4 -mb-4 mt-5 border-t border-line bg-surface px-4 py-3 shadow-[0_-8px_24px_-16px_rgba(39,38,35,0.25)] sm:-bottom-6 sm:-mx-6 sm:-mb-6 sm:px-6 xl:-bottom-8 xl:-mx-8 xl:-mb-8 xl:px-8">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
          <p
            role="status"
            className="hidden min-w-0 items-center gap-2 text-sm-label text-ink-2 sm:flex"
          >
            <span
              aria-hidden="true"
              className={`h-2 w-2 shrink-0 rounded-full ${footerStatus.dot}`}
            />
            <span className="truncate">{footerStatus.label}</span>
          </p>
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
            <Btn
              kind="default"
              type="button"
              className="w-full sm:w-auto sm:min-w-24"
              onClick={() => navigate('/products')}
            >
              {canEdit ? 'ยกเลิก' : 'กลับ'}
            </Btn>
            {canEdit && (
              <Btn
                kind="primary"
                type="submit"
                form={formId}
                className="w-full sm:w-auto sm:min-w-32"
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'กำลังบันทึก...' : 'บันทึกสินค้า'}
              </Btn>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={lossConfirmOpen}
        title="ยืนยันราคาขายต่ำกว่าทุน"
        description="ตรวจสอบต้นทุนและราคาขายอีกครั้งก่อนบันทึกสินค้า"
        tone="danger"
        onClose={() => setLossConfirmOpen(false)}
        footer={
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
            <Btn type="button" onClick={() => setLossConfirmOpen(false)}>
              กลับไปตรวจสอบ
            </Btn>
            <Btn
              kind="danger"
              type="button"
              onClick={() => {
                setLossConfirmOpen(false);
                saveProduct();
              }}
            >
              ยืนยันบันทึก
            </Btn>
          </div>
        }
      >
        <p className="text-body text-ink-2">
          มีราคาขายต่ำกว่าทุน <strong>{lossCount} หน่วย</strong>{' '}
          การขายหน่วยเหล่านี้จะเกิดผลขาดทุน
        </p>
      </Modal>
    </div>
  );
}
